/**
 * M7 S26: the story bridge (plan §2, S26; spec §1 rule 11, §10, §12). The one place that turns the story's
 * `StoryCommand`s into strategic worker commands and the worker's results back into Career changes.
 *
 * - **Commands (`toWorkerCommand`):** the story's commands are the worker's M7 and M5 commands with the same shapes,
 *   except the flat story `postWire`, which becomes the M5 `{ item }` shape with its outlet `source` (S17) and a
 *   severity of 1–3. Every `untilOnlineDay` is **rebased** from the Career's online-day count to the worker's
 *   (`onlineOffset` = worker − Career): on a load they agree (offset 0); a fresh Reach (an import, a lost companion)
 *   starts the worker's counter at 0 while the Career keeps its own, so a floor or claim state ends on the same
 *   online day either way.
 * - **Re-apply (`createStorySync`):** the idempotent list (`reapply.ts`) is sent in full on load and on a fresh Reach
 *   (`reset`), and as a diff on every story change (only commands not sent yet; a story flag the Career dropped is
 *   sent as `false`). Re-applied registrations carry `reapply: true` (no fee or finder's fee again, S20). The worker
 *   never records fired tier drops, so `storyTierDrop` is never re-applied: only `syncClock`'s pending drop sends it.
 * - **Settling (`settleStoryResult`):** after every story result the new flags schedule their callbacks
 *   (`scheduleNewFlagCallbacks`, S11) and the clock syncs (`syncClock`, S10: the pending tier drop once, the latched
 *   alerts, `setStoryClock`).
 * - **Online days (`storyOnlineDay`):** one per `AdvanceResult.onlineDays` (the client's `onOnlineDays`; catch-up days
 *   never count): `tickOnlineDay` with the callback, finale (surcharge), Okafor and aftermath (the Wire epilogue, S39 part 5) handlers merged, then the
 *   Blackmail stipend (`payStipend`, S39 part 6: into the Career wallet in the same write, with the worker's stability and
 *   gate stage), then the idleRamp's Wire item (`idleRampWireOnDay`), then `syncClock` (the Guardians floor's 1.3 → 1.8
 *   step, the ramp).
 * - **The absence (`StoryBridge.catchUp`):** `catchUpStory` (S6): the away-report warnings and any floor held past
 *   the absence (re-sent). The worker side lists its extended floors in `AwayReport.story` (`view/awayReport.ts`).
 * - **Autosave requests:** every `autosave` effect (fork commits, the finale, registrations) is handed to
 *   `requestAutosave` once per result.
 * - **Worker view (`storyWorldView`):** `StoryView` as the story's `StoryWorldView` (`corpFaction` is `'player'` once
 *   the corporation is founded; S26 widened the story type to match).
 *
 * The integration lane (S30) owns the live wiring: the story service, the event mapping (`StoryEvent`s, including
 * the `onlineDay` event for mission steps), `nudgeOnDay`, `recordVoteOutcome` and where the effects show.
 *
 * Event-driven, never per frame; it may allocate.
 */
import type { CareerAccess } from '../bridge/m2/career'
import type { Career } from '../career/types'
import { logger } from '../core/logger'
import type { AwayReport, CommandResult, StoryReactivationNote, StoryView, StrategicCommand } from '../strategic/types'
import { callbackHandlers, scheduleNewFlagCallbacks } from './callbacks'
import { idleRampWireOnDay, syncClock } from './clock'
import { catchUpStory, runtimeConfig, tickOnlineDay, type CatchUpData, type CountdownHandler, type TickOptions } from './countdowns'
import { storyData } from './data'
import { aftermathHandlers, type EpilogueFacts } from './finale/epilogue'
import { finaleHandlers } from './finale/ladder'
import { payStipend, type StipendView } from './finale/stipend'
import { okaforHandlers } from './okafor'
import { reapplyCommands, type ReapplyOptions } from './reapply'
import { storyOf } from './state'
import { STORY_FLAGS, type CountdownKind, type StoryCommand, type StoryEffect, type StoryResult, type StoryWorldView } from './types'

// ---------------------------------------------------------------------------------------------
// Commands
// ---------------------------------------------------------------------------------------------

function rebase(day: number, offset: number): number {
  const d = day + offset
  return d > 0 ? d : 0
}

/** The worker command for a story command, with every `untilOnlineDay` moved by `onlineOffset` (worker − Career). */
export function toWorkerCommand(cmd: StoryCommand, onlineOffset = 0): StrategicCommand {
  switch (cmd.type) {
    case 'postWire': {
      const severity = cmd.severity >= 3 ? 3 : cmd.severity <= 1 ? 1 : 2
      const item: Extract<StrategicCommand, { type: 'postWire' }>['item'] = { headline: cmd.headline, severity, source: cmd.source }
      if (cmd.systemId !== undefined) item.systemId = cmd.systemId
      return { type: 'postWire', item }
    }
    case 'setGrievance':
    case 'setAggressionFloor':
      return onlineOffset === 0 ? cmd : { ...cmd, untilOnlineDay: rebase(cmd.untilOnlineDay, onlineOffset) }
    case 'setClaimState':
      return onlineOffset === 0 || cmd.untilOnlineDay === null ? cmd : { ...cmd, untilOnlineDay: rebase(cmd.untilOnlineDay, onlineOffset) }
    default:
      return cmd
  }
}

/** The worker's `StoryView` as the story's `StoryWorldView`. */
export function storyWorldView(view: StoryView): StoryWorldView {
  return { ...view, reactivatedStations: [...view.reactivatedStations] }
}

/** True when the Career has story state the worker must hold (a claimed tier or a story block). */
export function wantsStoryReapply(career: Career): boolean {
  return (career.storyTier ?? 0) > 0 || career.story !== undefined
}

/**
 * The away report with the main thread's countdown warnings (`StoryBridge.catchUp`) in its story section, next to
 * the worker's extended floors. The same report when there are no warnings.
 */
export function withStoryWarnings(report: AwayReport, warnings: readonly string[]): AwayReport {
  if (warnings.length === 0) return report
  const story = report.story ?? { floorsExtended: [], warnings: [] }
  const merged = [...story.warnings]
  for (const w of warnings) if (!merged.includes(w)) merged.push(w)
  return { ...report, story: { floorsExtended: [...story.floorsExtended], warnings: merged } }
}

/** The autosave reasons a result asks for, once each, in order. */
export function autosaveReasons(effects: readonly StoryEffect[]): string[] {
  const out: string[] = []
  for (const e of effects) if (e.type === 'autosave' && !out.includes(e.reason)) out.push(e.reason)
  return out
}

// ---------------------------------------------------------------------------------------------
// Settling a result and the online day
// ---------------------------------------------------------------------------------------------

/**
 * A story result made whole: the callbacks of flags set between `before` and the result are scheduled, and the
 * clock syncs (its pending tier drop, once, then `setStoryClock`), appended after the result's own commands.
 */
export function settleStoryResult(before: Career, result: StoryResult<Career>): StoryResult<Career> {
  const scheduled = scheduleNewFlagCallbacks(before, result.career)
  const clock = syncClock(scheduled)
  return { career: clock.career, commands: [...result.commands, ...clock.commands], effects: [...result.effects, ...clock.effects] }
}

/** The epilogue's facts before the worker's story view is read (the gate as normal, no corporation, no ballots). */
export function defaultEpilogueFacts(): EpilogueFacts {
  return { stage: 'normal', corpFaction: null }
}

/**
 * The countdown handlers of every story module (S11 callbacks, S21 finale surcharge, S13 Okafor, S25 aftermath: the
 * Wire epilogue items with `facts` read when each falls due, and the Candle-dark event) plus `extra`.
 */
export function storyHandlers(
  extra: TickOptions['handlers'] = {},
  facts: () => EpilogueFacts = defaultEpilogueFacts,
): Partial<Record<CountdownKind, CountdownHandler>> {
  return { ...callbackHandlers(), ...finaleHandlers(), ...okaforHandlers(), ...aftermathHandlers(facts), ...extra }
}

/**
 * One online faction day: counts it and fires what fell due (`tickOnlineDay`), pays that day's Blackmail stipend
 * (`payStipend`; only while Blackmail is in force, and only with the worker's `stipend` view: none without one),
 * then the idleRamp's Wire item for that day and the clock sync. Call it once per online day (never for a catch-up
 * day): the day count, the wallet and `stipend.paid` move in one Career write, so a save and load never pays twice.
 */
export function storyOnlineDay(
  career: Career,
  handlers: TickOptions['handlers'] = storyHandlers(),
  stipend: StipendView | null = null,
): StoryResult<Career> {
  const tick = tickOnlineDay(career, { handlers })
  const pay = stipend === null ? null : payStipend(tick.career, stipend)
  const day: StoryResult<Career> =
    pay === null || pay.career === tick.career
      ? tick
      : { career: pay.career, commands: [...tick.commands, ...pay.commands], effects: [...tick.effects, ...pay.effects] }
  const wire = idleRampWireOnDay(day.career)
  const settled = settleStoryResult(career, day)
  return { career: settled.career, commands: [...wire, ...settled.commands], effects: settled.effects }
}

// ---------------------------------------------------------------------------------------------
// The re-apply sync (load, fresh Reach, every story change)
// ---------------------------------------------------------------------------------------------

/** What the re-apply reads of the world (the player's system for the hunt; POI → system for restarts). */
export type StorySyncContext = Pick<ReapplyOptions, 'systemId' | 'systemOfPoi'>

export interface StorySync {
  /** Worker online day − Career online days (0 until a fresh Reach sets it). */
  readonly onlineOffset: number
  /**
   * The worker commands to send for `career` now: the whole re-apply list on the first call after `reset`, else
   * only the commands not sent yet (and `setWorldFlag(key, false)` for a story flag the Career no longer has).
   */
  commands(career: Career, ctx?: StorySyncContext): StrategicCommand[]
  /** Forget what was sent (load, fresh Reach): the next `commands` call sends the whole list. */
  reset(): void
  /** A fresh Reach: the worker's online day now, so later `untilOnlineDay`s are rebased to it. */
  alignOnlineDays(workerOnlineDay: number, career: Career): void
  /** A story command as the worker gets it (rebased), remembered so the next diff doesn't repeat it. */
  toWorker(cmd: StoryCommand): StrategicCommand
}

function keyOf(cmd: StrategicCommand): string {
  return JSON.stringify(cmd)
}

const STORY_FLAG_KEYS: ReadonlySet<string> = new Set<string>(STORY_FLAGS)

/** The diffing re-apply sender (plan S26: "idempotent re-apply: sets, never adds"). */
export function createStorySync(): StorySync {
  let sent = new Set<string>()
  let flagsOn = new Set<string>()
  let offset = 0
  return {
    get onlineOffset(): number {
      return offset
    },
    commands(career: Career, ctx: StorySyncContext = {}): StrategicCommand[] {
      const out: StrategicCommand[] = []
      const nowOn = new Set<string>()
      for (const cmd of reapplyCommands(career, ctx)) {
        if (cmd.type === 'setWorldFlag' && STORY_FLAG_KEYS.has(cmd.key) && cmd.value === true) nowOn.add(cmd.key)
        const w = toWorkerCommand(cmd, offset)
        const key = keyOf(w)
        if (sent.has(key)) continue
        sent.add(key)
        out.push(w)
      }
      for (const key of flagsOn) {
        if (nowOn.has(key)) continue
        const off: StrategicCommand = { type: 'setWorldFlag', key, value: false }
        sent.delete(keyOf({ type: 'setWorldFlag', key, value: true }))
        out.push(off)
      }
      flagsOn = nowOn
      return out
    },
    reset(): void {
      sent = new Set<string>()
      flagsOn = new Set<string>()
    },
    alignOnlineDays(workerOnlineDay: number, career: Career): void {
      offset = workerOnlineDay - storyOf(career).onlineDays
    },
    toWorker(cmd: StoryCommand): StrategicCommand {
      const w = toWorkerCommand(cmd, offset)
      if (cmd.type !== 'storyTierDrop' && cmd.type !== 'postWire') sent.add(keyOf(w))
      return w
    },
  }
}

// ---------------------------------------------------------------------------------------------
// The bridge
// ---------------------------------------------------------------------------------------------

/** What the bridge needs of the strategic client (`StrategicClient` satisfies it). */
export interface StoryClientPort {
  command(cmd: StrategicCommand): Promise<CommandResult>
  /** Online faction days that ended (`AdvanceResult.onlineDays`); absent = the caller calls `onlineDays` itself. */
  onOnlineDays?(fn: (days: number) => void): () => void
  /** S43: the worker's reactivation notes (`AdvanceResult.storyReactivations`); absent = none (tests, fakes). */
  onStoryReactivations?(fn: (notes: readonly StoryReactivationNote[]) => void): () => void
}

export interface StoryBridgeDeps {
  /** The Career's read and write path; null before a career runs. */
  access: () => CareerAccess | null
  client: StoryClientPort
  /** Shared with the charter career bridge (it sends the re-apply diff on every story change). */
  sync?: StorySync
  /** The world the re-apply reads. */
  world?: () => StorySyncContext
  /** One request per autosave effect (S30 writes the slot through the existing save path). */
  requestAutosave?: (reason: string) => void
  /** UI work (comms, Wire, mission HUD, away warnings; S30 routes them). */
  onEffects?: (effects: readonly StoryEffect[]) => void
  /** Extra countdown handlers for the online day (merged over the story modules' own). */
  handlers?: TickOptions['handlers']
  /** The epilogue's sim facts today (the worker's gate stage, corporation, ballots); default `defaultEpilogueFacts`. */
  epilogueFacts?: () => EpilogueFacts
  /**
   * The worker's stability and gate stage for the Blackmail stipend (S39 part 6), read once per online day; null (or
   * absent) = no view yet, so no stipend that day.
   */
  stipendView?: () => StipendView | null
  /** Tests pass their own. */
  catchUpData?: CatchUpData
}

export interface StoryBridge {
  readonly sync: StorySync
  /**
   * Runs a story function on the Career through its write path, settles it (new-flag callbacks, the clock), sends
   * its commands, routes its effects and autosave requests. Null when no career runs or the write was refused.
   */
  apply(name: string, fn: (career: Career) => StoryResult<Career>): StoryResult<Career> | null
  /** `days` online faction days ended (the client's `onOnlineDays` calls it when subscribed). */
  onlineDays(days: number): void
  /** An absence of `simulatedDays` faction days: returns the away-report warnings and re-sends moved floors. */
  catchUp(simulatedDays: number): string[]
  /**
   * Load or a fresh Reach: sends the whole re-apply list. With the worker's `StoryView` (a fresh Reach), the online
   * days are rebased to the worker's counter first.
   */
  reapply(view?: Pick<StoryView, 'onlineDay'>): void
  dispose(): void
}

export function createStoryBridge(deps: StoryBridgeDeps): StoryBridge {
  const sync = deps.sync ?? createStorySync()
  const handlers = storyHandlers(deps.handlers, deps.epilogueFacts)
  const world = (): StorySyncContext => deps.world?.() ?? {}

  function send(cmd: StrategicCommand): void {
    void deps.client.command(cmd).then(
      (r) => {
        if (!r.ok) logger.warn(`story bridge: ${cmd.type} refused (${r.error})`)
      },
      (err: unknown) => logger.warn(`story bridge: ${cmd.type} failed`, err),
    )
  }

  function deliver(result: StoryResult<Career>): void {
    for (const cmd of result.commands) send(sync.toWorker(cmd))
    if (result.effects.length > 0) deps.onEffects?.(result.effects)
    if (deps.requestAutosave !== undefined) for (const reason of autosaveReasons(result.effects)) deps.requestAutosave(reason)
  }

  /** Writes `run(career)` through the Career's write path and delivers it; null when refused. */
  function write(name: string, run: (career: Career) => StoryResult<Career>): StoryResult<Career> | null {
    const access = deps.access()
    if (access === null) return null
    const box: { result: StoryResult<Career> | null } = { result: null }
    const out = access.apply(name, (c) => {
      const r = run(c)
      box.result = r
      return r.career
    })
    const result = box.result
    if (!out.ok) {
      logger.warn(`story bridge: ${name} not applied`, out.error)
      return null
    }
    if (result === null) return null
    deliver(result)
    return result
  }

  const unsubscribe = deps.client.onOnlineDays?.((days) => bridge.onlineDays(days)) ?? null

  const bridge: StoryBridge = {
    sync,
    apply(name, fn) {
      return write(name, (c) => settleStoryResult(c, fn(c)))
    },
    onlineDays(days) {
      for (let i = 0; i < days; i++) if (write('storyOnlineDay', (c) => storyOnlineDay(c, handlers, deps.stipendView?.() ?? null)) === null) return
    },
    catchUp(simulatedDays) {
      const data = deps.catchUpData ?? { runtime: runtimeConfig(), floorExtensionOnlineDays: storyData().finale.floorExtensionOnlineDays }
      const box: { warnings: string[] } = { warnings: [] }
      write('storyCatchUp', (c) => {
        const r = catchUpStory(c, simulatedDays, data)
        box.warnings = r.warnings
        return r
      })
      return box.warnings
    },
    reapply(view) {
      const access = deps.access()
      if (access === null) return
      const career = access.career()
      if (view !== undefined) sync.alignOnlineDays(view.onlineDay, career)
      sync.reset()
      if (!wantsStoryReapply(career)) return
      for (const cmd of sync.commands(career, world())) send(cmd)
    },
    dispose() {
      unsubscribe?.()
    },
  }
  return bridge
}
